import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, ChevronDown, Rocket } from 'lucide-react';
import { ComponentType, CSSProperties, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { BookTextIcon } from '@/Domain/components/icons/BookTextIcon';
import { ConnectIcon } from '@/Domain/components/icons/ConnectIcon';
import { cn } from '@/Domain/utils/cn';
import {
  type AppOption,
  type ChannelOption,
  APPS,
  CHANNEL_CHAT_EMBED,
  CHANNEL_CHAT_PAGE,
  CHANNEL_CHAT_WIDGET,
  CHANNELS,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';
import type { GettingStartedProgressDto } from '@/UI/GettingStarted/components/useGettingStartedOnboarding';

type Glyph = ComponentType<{ className?: string; style?: CSSProperties }>;

type StepId = 'knowledge_base' | 'external_apps' | 'deployment';

const ALWAYS_SHOWN_DEPLOYMENT_CHANNELS = [
  CHANNEL_CHAT_WIDGET,
  CHANNEL_CHAT_EMBED,
  CHANNEL_CHAT_PAGE,
];

export const PostOnboardingChecklist = () => {
  const { t } = useTranslation('onboarding_progress');
  const navigate = useNavigate();

  const { data: progress, isLoading } = useQuery<GettingStartedProgressDto | null>({
    queryKey: [QueryKey.gettingStarted.progress],
  });

  const selectedApps = useMemo<AppOption[]>(() => {
    const picked = new Set(progress?.external_apps_selection ?? []);
    return APPS.filter((app) => picked.has(app.id));
  }, [progress?.external_apps_selection]);

  const deploymentChannelsToShow = useMemo<ChannelOption[]>(() => {
    const idsToShow = new Set<string>([
      ...ALWAYS_SHOWN_DEPLOYMENT_CHANNELS,
      ...(progress?.deployment_selection ?? []),
    ]);
    return CHANNELS.filter((channel) => idsToShow.has(channel.id));
  }, [progress?.deployment_selection]);

  const hasUserDeploymentPicks = (progress?.deployment_selection ?? []).length > 0;

  const [expanded, setExpanded] = useState<StepId | null>('knowledge_base');
  const toggle = (id: StepId) => setExpanded((current) => (current === id ? null : id));

  const go = (routePath: string, queryParams?: Record<string, string>) => {
    const qs = queryParams ? new URLSearchParams(queryParams).toString() : '';
    navigate(qs ? `${routePath}?${qs}` : routePath, { viewTransition: true });
  };

  const goToItem = (item: SelectionItem) => {
    const params: Record<string, string> = { ...(item.queryParams ?? {}) };
    if (item.autoOpenModal) params[OPEN_MODAL_QUERY_PARAM] = item.id;
    go(item.routePath, Object.keys(params).length ? params : undefined);
  };

  const steps: Array<{
    id: StepId;
    icon: ReactNode;
    title: string;
    description: string;
    content: ReactNode;
  }> = [
    {
      id: 'knowledge_base',
      icon: <BookTextIcon size={16} className="text-muted-foreground" />,
      title: t('next_steps.knowledge_base.title'),
      description: t('next_steps.knowledge_base.description'),
      content: (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => go(IndexPath.knowledgeBase.root)}
          className="self-start"
          data-testid="next-steps-knowledge-base-cta"
        >
          {t('next_steps.knowledge_base.cta')}
          <ArrowUpRight className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: 'external_apps',
      icon: <ConnectIcon size={16} className="text-muted-foreground" />,
      title: t('next_steps.external_apps.title'),
      description: t('next_steps.external_apps.description'),
      content:
        selectedApps.length > 0 ? (
          <SelectionList
            items={selectedApps}
            ctaLabel={t('next_steps.cta_connect')}
            onItemClick={goToItem}
            testIdPrefix="next-steps-external-apps-item"
          />
        ) : (
          <EmptySelection
            ctaLabel={t('next_steps.external_apps.empty_cta')}
            onClick={() => go(IndexPath.actions)}
            testId="next-steps-external-apps-empty-cta"
          />
        ),
    },
    {
      id: 'deployment',
      icon: <Rocket className="size-4 text-muted-foreground" />,
      title: t('next_steps.deployment.title'),
      description: t('next_steps.deployment.description'),
      content: (
        <div className="flex flex-col gap-3">
          <SelectionList
            items={deploymentChannelsToShow}
            ctaLabel={t('next_steps.cta_set_up')}
            onItemClick={goToItem}
            testIdPrefix="next-steps-deployment-item"
          />
          {!hasUserDeploymentPicks && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => go(IndexPath.channels)}
              className="self-start"
              data-testid="next-steps-deployment-empty-cta"
            >
              {t('next_steps.deployment.empty_cta')}
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="post-onboarding-checklist">
      <h2 className="text-sm font-semibold text-foreground">{t('next_steps.heading')}</h2>
      <div className="flex flex-col gap-2">
        {steps.map((step) => {
          const isOpen = expanded === step.id;
          return (
            <div
              key={step.id}
              className="rounded-xl border border-border bg-card overflow-hidden"
              data-testid={`next-steps-row-${step.id}`}
            >
              <button
                type="button"
                onClick={() => toggle(step.id)}
                className="w-full flex items-center gap-3 p-3 text-left transition-colors cursor-pointer hover:bg-accent"
                aria-expanded={isOpen}
                data-testid={`next-steps-row-header-${step.id}`}
              >
                <div className="flex size-8 items-center justify-center rounded-lg bg-muted shrink-0">
                  {step.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{step.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{step.description}</p>
                </div>
                <ChevronDown
                  className={cn(
                    'size-4 text-muted-foreground transition-transform duration-200 shrink-0',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-3 pb-3 pt-2">{step.content}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface SelectionItem {
  id: string;
  label: string;
  icon: Glyph;
  iconColor?: string;
  routePath: string;
  autoOpenModal: boolean;
  queryParams?: Record<string, string>;
}

const SelectionList = ({
  items,
  ctaLabel,
  onItemClick,
  testIdPrefix,
}: {
  items: SelectionItem[];
  ctaLabel: string;
  onItemClick: (item: SelectionItem) => void;
  testIdPrefix: string;
}) => (
  <ul className="flex flex-col">
    {items.map((item) => (
      <li
        key={item.id}
        className="flex items-center gap-2 py-1"
        data-testid={`${testIdPrefix}-${item.id}`}
      >
        <div className="flex size-6 items-center justify-center shrink-0">
          <item.icon
            className="size-4"
            style={item.iconColor ? { color: item.iconColor } : undefined}
          />
        </div>
        <span className="flex-1 text-sm text-foreground truncate">{item.label}</span>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onItemClick(item)}
          data-testid={`${testIdPrefix}-${item.id}-cta`}
        >
          {ctaLabel}
          <ArrowUpRight className="h-4 w-4" />
        </Button>
      </li>
    ))}
  </ul>
);

const EmptySelection = ({
  ctaLabel,
  onClick,
  testId,
}: {
  ctaLabel: string;
  onClick: () => void;
  testId: string;
}) => (
  <Button
    variant="secondary"
    size="sm"
    onClick={onClick}
    className="self-start"
    data-testid={testId}
  >
    {ctaLabel}
    <ArrowUpRight className="h-4 w-4" />
  </Button>
);
